import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, Plus, Pencil, Copy, Trash2, Layers, PlaySquare } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CourseOrdersTab, CourseSalesTab } from "@/components/courses/CourseSalesPanel";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "Todos los niveles"];

const STATUS_BADGE = {
  draft: { label: "Borrador", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  published: { label: "Publicado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  archived: { label: "Archivado", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function Courses() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", subtitle: "", price: "", level: "" });

  const load = async () => {
    try {
      const r = await api.get("/courses");
      setCourses(r.data || []);
    } catch {
      toast.error("Error cargando cursos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createCourse = async () => {
    if (!form.title.trim()) { toast.error("Poné un título"); return; }
    setCreating(true);
    try {
      const r = await api.post("/courses", {
        title: form.title,
        subtitle: form.subtitle || null,
        price: form.price === "" ? null : Number(form.price),
        level: form.level || null,
      });
      toast.success("Curso creado como borrador");
      setCreateOpen(false);
      navigate(`/dashboard/courses/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando el curso");
    } finally {
      setCreating(false);
    }
  };

  const duplicateCourse = async (c) => {
    try {
      await api.post(`/courses/${c.id}/duplicate`);
      toast.success("Curso duplicado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteCourse = async (c) => {
    const ok = await confirm({
      title: "¿Eliminar curso?",
      description: `Se borrará "${c.title}" con todos sus módulos y lecciones. Esta acción no se puede deshacer.`,
      confirmText: "Sí, eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success("Curso eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <PageHeader title="Cursos On-Demand" subtitle="Cursos grabados que los estudiantes compran y estudian a su ritmo." />
        <Button className="btn-brand" onClick={() => { setForm({ title: "", subtitle: "", price: "", level: "" }); setCreateOpen(true); }} data-testid="new-course-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuevo curso
        </Button>
      </div>

      <Tabs defaultValue="courses">
        <TabsList data-testid="courses-tabs">
          <TabsTrigger value="courses" data-testid="tab-courses-list">Cursos</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-course-orders">Órdenes</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-course-sales">Ventas</TabsTrigger>
        </TabsList>
        <TabsContent value="courses" className="mt-4">
      {loading ? (
        <div className="text-slate-500 text-sm">Cargando…</div>
      ) : courses.length === 0 ? (
        <Card className="p-10 text-center" data-testid="courses-empty">
          <GraduationCap className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 font-medium">Aún no hay cursos</div>
          <p className="text-sm text-slate-500 mt-1">Creá tu primer curso on-demand: módulos con videos, presentaciones, prácticas y quizzes.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => {
            const sb = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
            return (
              <Card key={c.id} className="overflow-hidden flex flex-col" data-testid={`course-card-${c.id}`}>
                <button type="button" onClick={() => navigate(`/dashboard/courses/${c.id}`)} className="relative aspect-video bg-slate-100 block w-full text-left">
                  {c.cover_url ? (
                    <img src={`${backendUrl}${c.cover_url}`} alt={c.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PlaySquare className="h-10 w-10 text-slate-300" />
                    </div>
                  )}
                  <Badge className={`absolute top-2 left-2 ${sb.cls}`}>{sb.label}</Badge>
                </button>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="font-semibold text-slate-900 leading-snug">{c.title}</div>
                  {c.subtitle && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.subtitle}</div>}
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                    <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.module_count} módulos · {c.lesson_count} lecciones</span>
                    {c.level && <Badge variant="outline" className="text-[10px]">{c.level}</Badge>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="font-mono font-semibold text-slate-900">{c.price === 0 ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Gratis</Badge> : c.price != null ? `$${Number(c.price).toFixed(2)}` : <span className="text-slate-400 text-xs">Sin precio</span>}</div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(`/dashboard/courses/${c.id}`)} data-testid={`edit-course-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" title="Duplicar" onClick={() => duplicateCourse(c)} data-testid={`dup-course-${c.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:bg-red-50" title="Eliminar" onClick={() => deleteCourse(c)} data-testid={`del-course-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
        </TabsContent>
        <TabsContent value="orders" className="mt-4"><CourseOrdersTab /></TabsContent>
        <TabsContent value="sales" className="mt-4"><CourseSalesTab /></TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-course-dialog">
          <DialogHeader><DialogTitle>Nuevo curso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Español desde cero — Nivel A1" data-testid="course-title-input" />
            </div>
            <div>
              <Label className="text-xs">Subtítulo</Label>
              <Textarea rows={2} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Frase corta que vende el curso" data-testid="course-subtitle-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Precio (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="149" data-testid="course-price-input" />
              </div>
              <div>
                <Label className="text-xs">Nivel</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="course-level-select"><SelectValue placeholder="Nivel" /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button className="btn-brand" onClick={createCourse} disabled={creating} data-testid="create-course-submit">
              {creating ? "Creando…" : "Crear y editar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
